var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _TvSessionConvertor_instances, _TvSessionConvertor_findNextWeekday, _TvSessionConvertor_formatHolidayDate;
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DayPeriods } from '@shared/utils/Session/DayPeriods';
import { TvTimeZoneHelper } from '../Helpers/TvTimeZoneHelper';
import { TvFullSession } from '../TradingViewPrimitives/TvConstants';
import { TvSessionIdEnum } from '../TradingViewPrimitives/TvEnums';
import { TvSubsessionInfo } from '../TradingViewPrimitives/TvSubsessionInfo';
import { DateTime } from 'luxon';
export class TvSessionConvertor {
    constructor() {
        _TvSessionConvertor_instances.add(this);
    }
    // TODO: fix use Day Start Offset
    convertToTvSession(session, dayPeriodFilter, isAllowOTHTrading) {
        const sessionPeriods = session.getSessionPeriods();
        const timeZoneId = session.TimeZoneID;
        const timeOffset = session.TimeOffset;
        if (isNullOrUndefined(sessionPeriods) || sessionPeriods.length == 0) {
            return TvFullSession;
        }
        const DAYS_ON_A_WEEK = 7;
        let result = '';
        for (let i = 0; i < DAYS_ON_A_WEEK; i++) {
            let daySchedule = this.getSessionPeriodsSchedule(sessionPeriods, timeZoneId, timeOffset, i, dayPeriodFilter, isAllowOTHTrading);
            if (daySchedule) {
                daySchedule += ':' + (i + 1);
                if (i + 1 < DAYS_ON_A_WEEK) {
                    daySchedule += '|';
                }
                result += daySchedule;
            }
        }
        if (result && result.endsWith('|')) {
            result = result.slice(0, -1);
        }
        return result;
    }
    getSessionPeriodsSchedule(sessionPeriods, timeZoneID, timeOffset, dayOfWeek, dayPeriodFilter, isAllowOTHTrading) {
        let begin;
        let end;
        for (const element of sessionPeriods) {
            const sessionPeriod = element;
            if (!isAllowOTHTrading && sessionPeriod.isBeforeOrAfterMarket()) {
                continue;
            }
            if (dayPeriodFilter.length > 0 && !dayPeriodFilter.includes(sessionPeriod.dayPeriod)) {
                continue;
            }
            const beginMS = sessionPeriod.GetBegin(dayOfWeek).getTime();
            const endMS = sessionPeriod.GetEnd(dayOfWeek).getTime();
            if (beginMS === endMS) {
                continue;
            }
            const timeZone = TvTimeZoneHelper.getTimeZoneForTV(timeZoneID);
            const daytimeOffset = timeOffset;
            if (!begin) {
                begin = this.convertTimeToTVSessionPartString(timeZone, beginMS, daytimeOffset, dayOfWeek);
            }
            end = this.convertTimeToTVSessionPartString(timeZone, endMS, daytimeOffset, dayOfWeek);
        }
        if (!begin || !end) {
            return '';
        }
        const periodSchedule = begin + '-' + end;
        return periodSchedule;
    }
    convertTimeToTVSessionPartString(timeZone, milliseconds, dayStartOffset, dayOfWeek) {
        const nearestDay = __classPrivateFieldGet(this, _TvSessionConvertor_instances, "m", _TvSessionConvertor_findNextWeekday).call(this, dayOfWeek, timeZone);
        const nearestDayWitOffset = nearestDay.plus({ minutes: dayStartOffset });
        const nearestDayWithTime = nearestDayWitOffset.plus({ milliseconds: milliseconds });
        const formattedTime = nearestDayWithTime.toFormat('HHmm');
        if (nearestDayWithTime < nearestDay) {
            return formattedTime + 'F';
        }
        return formattedTime;
    }
    getHolidays(session) {
        const holidays = session.getHolidays().values();
        const holidaysDateArray = [];
        for (const holiday of holidays) {
            const formattedDate = __classPrivateFieldGet(this, _TvSessionConvertor_instances, "m", _TvSessionConvertor_formatHolidayDate).call(this, holiday.year, holiday.month, holiday.day);
            if (holiday.IsNotWorking() || holiday.IsShorted()) {
                holidaysDateArray.push(formattedDate);
            }
        }
        return holidaysDateArray.join(',');
    }
    getHolidaysCorrections(session, dayPeriodFilter, isAllowOTHTrading) {
        const timeZoneId = session.TimeZoneID;
        const timeOffset = session.TimeOffset;
        const holidays = session.getHolidays().values();
        const holidaysSessionArray = [];
        for (const holiday of holidays) {
            const formattedDate = __classPrivateFieldGet(this, _TvSessionConvertor_instances, "m", _TvSessionConvertor_formatHolidayDate).call(this, holiday.year, holiday.month, holiday.day);
            if (holiday.IsShorted()) {
                const dayOfWeek = holiday.getDayOfWeek();
                const tvSessionString = this.getSessionPeriodsSchedule(holiday.sessionPeriods, timeZoneId, timeOffset, dayOfWeek, dayPeriodFilter, isAllowOTHTrading);
                if (tvSessionString) {
                    holidaysSessionArray.push(`${tvSessionString}:${formattedDate}`);
                }
            }
        }
        return holidaysSessionArray.join(';');
    }
    getSubsessions(session, isAllowOTHTrading) {
        const subsessions = [];
        const regularSubsession = new TvSubsessionInfo();
        regularSubsession.description = 'Regular Trading Hours';
        regularSubsession.id = TvSessionIdEnum.regular;
        regularSubsession.session = this.convertToTvSession(session, [DayPeriods.MAIN], isAllowOTHTrading);
        regularSubsession.setCorrection(this.getHolidaysCorrections(session, [DayPeriods.MAIN], isAllowOTHTrading));
        subsessions.push(regularSubsession);
        const extendedSubsession = new TvSubsessionInfo();
        extendedSubsession.description = 'Extended Trading Hours';
        extendedSubsession.id = TvSessionIdEnum.extended;
        extendedSubsession.session = this.convertToTvSession(session, [], isAllowOTHTrading);
        extendedSubsession.setCorrection(this.getHolidaysCorrections(session, [], isAllowOTHTrading));
        subsessions.push(extendedSubsession);
        const preMarketSession = this.convertToTvSession(session, [DayPeriods.BEFORE_MARKET, DayPeriods.PRE_OPEN], isAllowOTHTrading);
        if (preMarketSession) {
            const premarketSubsession = new TvSubsessionInfo();
            premarketSubsession.description = 'Premarket';
            premarketSubsession.id = TvSessionIdEnum.premarket;
            premarketSubsession.session = preMarketSession;
            premarketSubsession.setCorrection(this.getHolidaysCorrections(session, [DayPeriods.BEFORE_MARKET, DayPeriods.PRE_OPEN], isAllowOTHTrading));
            subsessions.push(premarketSubsession);
        }
        const postMarketSession = this.convertToTvSession(session, [DayPeriods.POST_CLOSE, DayPeriods.AFTER_MARKET], isAllowOTHTrading);
        if (postMarketSession) {
            const postmarketSubsession = new TvSubsessionInfo();
            postmarketSubsession.description = 'Postmarket';
            postmarketSubsession.id = TvSessionIdEnum.postmarket;
            postmarketSubsession.session = postMarketSession;
            postmarketSubsession.setCorrection(this.getHolidaysCorrections(session, [DayPeriods.POST_CLOSE, DayPeriods.AFTER_MARKET], isAllowOTHTrading));
            subsessions.push(postmarketSubsession);
        }
        return subsessions;
    }
}
_TvSessionConvertor_instances = new WeakSet(), _TvSessionConvertor_findNextWeekday = function _TvSessionConvertor_findNextWeekday(targetWeekday, timeZone) {
    const now = DateTime.local();
    // Определите, сколько дней осталось до целевого дня недели
    let daysTillTarget = targetWeekday - now.weekday + 1;
    if (daysTillTarget <= 0) {
        daysTillTarget += 7;
    }
    const nextTargetDay = now.plus({ days: daysTillTarget });
    const year = nextTargetDay.year;
    const month = nextTargetDay.month;
    const day = nextTargetDay.day;
    const nextTargetDayInSessionTimeZone = DateTime.fromObject({ year, month, day }, { zone: timeZone });
    return nextTargetDayInSessionTimeZone;
}, _TvSessionConvertor_formatHolidayDate = function _TvSessionConvertor_formatHolidayDate(year, month, day) {
    const yyyy = year.toString();
    const mm = month.toString();
    const dd = day.toString();
    return yyyy + (mm[1] ? mm : '0' + mm[0]) + (dd[1] ? dd : '0' + dd[0]);
};
